import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import type { DropResult } from "@hello-pangea/dnd";
import { Close, DragIndicator } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  styled,
  Typography,
} from "@mui/material";
import React from "react";

interface ColumnMenuProps {
  columns: Array<{ fieldName: string; label: string }>;
  visibleColumns: string[];
  hiddenColumns: string[];
  handleColumnToggle: (fieldName: string) => void;
  handleColumnMove: (fromIndex: number, toIndex: number) => void;
  tableId?: string;
  handleSaveAsDefault: () => void;
  isCurrentSelectionDefault: boolean;
  onClose?: () => void;
}

const MenuContainer = styled(Box)(() => ({
  padding: 16,
  minWidth: 250,
}));

const ColumnItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isHidden" && prop !== "isDragging",
})<{ isHidden?: boolean; isDragging?: boolean }>(
  ({ theme, isHidden, isDragging }) => ({
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: isHidden ? "transparent" : theme.palette.action.hover,
    opacity: isHidden ? 0.7 : 1,
    position: "relative",
    zIndex: isDragging ? 1 : 0,
  }),
);

const DragHandle = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(1),
  display: "flex",
  opacity: 0.3,
}));

const DragHandleActive = styled(DragHandle)({
  opacity: 1,
  cursor: "grab",
  "&:active": {
    cursor: "grabbing",
  },
});

const FormLabel = styled(FormControlLabel)(() => ({
  flexGrow: 1,
  margin: 0,
}));

const SaveButtonContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(1),
  borderTop: 1,
  borderColor: theme.palette.divider,
}));

export const ColumnMenu: React.FC<ColumnMenuProps> = ({
  columns,
  visibleColumns,
  hiddenColumns,
  handleColumnToggle,
  handleColumnMove,
  tableId,
  handleSaveAsDefault,
  isCurrentSelectionDefault,
  onClose,
}) => (
  <MenuContainer>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 0.5,
      }}
    >
      <Typography variant="subtitle2">Columns</Typography>
      {onClose && (
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="close column selector"
        >
          <Close fontSize="small" />
        </IconButton>
      )}
    </Box>
    <Typography
      variant="caption"
      display="block"
      color="text.secondary"
      gutterBottom
    >
      Check columns to show/hide them. Drag columns to reorder.
    </Typography>

    <DragDropContext
      onDragEnd={(result: DropResult) => {
        if (!result.destination) return;
        handleColumnMove(result.source.index, result.destination.index);
      }}
    >
      <Droppable droppableId="visible-columns">
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {/* Render visible columns (which are draggable) first */}
            {visibleColumns.map((fieldName, index) => {
              const column = columns.find((col) => col.fieldName === fieldName);
              if (!column) return null;

              return (
                <Draggable
                  key={fieldName}
                  draggableId={fieldName}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <ColumnItem
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      isDragging={snapshot.isDragging}
                    >
                      <DragHandleActive {...provided.dragHandleProps}>
                        <DragIndicator fontSize="small" />
                      </DragHandleActive>
                      <FormLabel
                        control={
                          <Checkbox
                            checked={true}
                            onChange={() =>
                              handleColumnToggle(column.fieldName)
                            }
                            size="small"
                          />
                        }
                        label={column.label}
                      />
                    </ColumnItem>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}

            {/* Render hidden columns (non-draggable) in a second separate container */}
            <Box sx={{ position: "relative", zIndex: 0 }}>
              {hiddenColumns.map((fieldName) => {
                const column = columns.find(
                  (col) => col.fieldName === fieldName,
                );
                if (!column) return null;

                return (
                  <ColumnItem key={fieldName} isHidden>
                    <DragHandle>
                      <DragIndicator fontSize="small" />
                    </DragHandle>
                    <FormLabel
                      control={
                        <Checkbox
                          checked={false}
                          onChange={() => handleColumnToggle(column.fieldName)}
                          size="small"
                        />
                      }
                      label={column.label}
                    />
                  </ColumnItem>
                );
              })}
            </Box>
          </div>
        )}
      </Droppable>
    </DragDropContext>
    {tableId && (
      <SaveButtonContainer>
        <Button
          onClick={handleSaveAsDefault}
          size="small"
          variant={isCurrentSelectionDefault ? "outlined" : "contained"}
          fullWidth
          disabled={isCurrentSelectionDefault}
        >
          {isCurrentSelectionDefault ? "Saved as Default" : "Save as Default"}
        </Button>
      </SaveButtonContainer>
    )}
  </MenuContainer>
);
